import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack
} from '@mui/material';

import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ComputerIcon from '@mui/icons-material/Computer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CampaignIcon from '@mui/icons-material/Campaign';
import FilterListIcon from '@mui/icons-material/FilterList';
import AnimatedNumber from '../../components/common/AnimatedNumber';

// Renk paleti
const COLORS = ['#3f51b5', '#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#e91e63', '#00bcd4'];
const SEGMENT_COLORS = {
  'Yüksek Değerli': '#4caf50',
  'Orta Değerli': '#2196f3',
  'Yeni Müşteri': '#ff9800',
  'Riskli': '#f44336',
  'Uykuda': '#9c27b0'
};

// Kategori renkleri
const CATEGORY_COLORS = {
  'Elektronik': '#3f51b5',
  'Giyim': '#4caf50',
  'Ev Eşyaları': '#ff9800',
  'Kozmetik': '#e91e63',
  'Gıda': '#9c27b0',
  'Aksesuar': '#00bcd4'
};

// Kanal renkleri
const CHANNEL_COLORS = {
  'Web': '#3f51b5',
  'Mobil': '#4caf50',
  'Mağaza': '#ff9800',
  'Sosyal Medya': '#e91e63',
  'Çağrı Merkezi': '#9c27b0'
};

// Örnek müşteri davranış analizi verileri
const generateCustomerData = () => {
  // Farklı segmentler
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  const segmentDistribution = [0.15, 0.35, 0.25, 0.15, 0.1]; // Segment dağılımı
  
  // Örnek müşteri veri seti oluştur
  const customers = Array.from({ length: 1000 }, (_, i) => {
    // Segment belirleme
    let segmentIndex = 0;
    let randomVal = Math.random();
    let cumulative = 0;
    
    for (let j = 0; j < segmentDistribution.length; j++) {
      cumulative += segmentDistribution[j];
      if (randomVal <= cumulative) {
        segmentIndex = j;
        break;
      }
    }
    
    const segment = segments[segmentIndex];
    
    // Segment'e göre müşteri değeri belirleme
    let monetaryValue = 0;
    let frequency = 0;
    let recency = 0;
    let retentionRate = 0;
    let engagementScore = 0;
    
    if (segment === 'Yüksek Değerli') {
      monetaryValue = 5000 + Math.random() * 5000;
      frequency = 15 + Math.random() * 15;
      recency = Math.random() * 7;
      retentionRate = 85 + Math.random() * 15;
      engagementScore = 85 + Math.random() * 15;
    } else if (segment === 'Orta Değerli') {
      monetaryValue = 1500 + Math.random() * 3500;
      frequency = 5 + Math.random() * 10;
      recency = 7 + Math.random() * 14;
      retentionRate = 60 + Math.random() * 25;
      engagementScore = 60 + Math.random() * 25;
    } else if (segment === 'Yeni Müşteri') {
      monetaryValue = 500 + Math.random() * 1000;
      frequency = 1 + Math.random() * 3;
      recency = Math.random() * 10;
      retentionRate = 20 + Math.random() * 40;
      engagementScore = 20 + Math.random() * 30;
    } else if (segment === 'Riskli') {
      monetaryValue = 1000 + Math.random() * 2000;
      frequency = 4 + Math.random() * 8;
      recency = 30 + Math.random() * 30;
      retentionRate = 30 + Math.random() * 30;
      engagementScore = 20 + Math.random() * 40;
    } else { // Uykuda
      monetaryValue = 500 + Math.random() * 1500;
      frequency = 2 + Math.random() * 5;
      recency = 60 + Math.random() * 30;
      retentionRate = 10 + Math.random() * 20;
      engagementScore = 5 + Math.random() * 15;
    }
    
    // Müşteri profili oluşturma
    return {
      id: `CUST${String(i+1).padStart(5, '0')}`,
      name: `Müşteri ${i+1}`,
      segment,
      monetaryValue: Math.round(monetaryValue),
      frequency: Math.round(frequency),
      recency: Math.round(recency),
      retentionRate: Math.round(retentionRate),
      engagementScore: Math.round(engagementScore),
      firstPurchaseDate: randomDate(new Date(2020, 0, 1), new Date(2023, 5, 30)),
      lastPurchaseDate: randomDate(new Date(2023, 6, 1), new Date()),
      preferredChannel: randomItem(['Web', 'Mobil', 'Mağaza', 'Sosyal Medya', 'Çağrı Merkezi']),
      preferredCategories: generatePreferredCategories(),
      purchaseHistory: generatePurchaseHistory(segment),
      churnRisk: calculateChurnRisk(segment, recency, frequency, retentionRate),
      customerLifetimeValue: calculateCLV(monetaryValue, frequency, retentionRate),
      nextPurchasePrediction: predictNextPurchase(segment, recency),
      recommendedProducts: generateRecommendedProducts(segment)
    };
  });
  
  return customers;
};

// Rastgele tarih üretme yardımcı fonksiyonu
function randomDate(start, end) {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime())).toISOString().split('T')[0];
}

// Rastgele öğe seçme yardımcı fonksiyonu
function randomItem(array) {
  return array[Math.floor(Math.random() * array.length)];
}

// Tercih edilen kategoriler oluşturma
function generatePreferredCategories() {
  const categories = ['Elektronik', 'Giyim', 'Ev Eşyaları', 'Kozmetik', 'Gıda', 'Aksesuar'];
  const count = 1 + Math.floor(Math.random() * 3); // 1-3 kategori
  const selected = [];
  
  // Rastgele benzersiz kategoriler seç
  while (selected.length < count) {
    const category = randomItem(categories);
    if (!selected.includes(category)) {
      selected.push(category);
    }
  }
  
  return selected;
}

// Satın alma geçmişi oluşturma
function generatePurchaseHistory(segment) {
  const purchaseCount = segment === 'Yüksek Değerli' ? 10 + Math.floor(Math.random() * 15) :
                       segment === 'Orta Değerli' ? 5 + Math.floor(Math.random() * 10) :
                       segment === 'Yeni Müşteri' ? 1 + Math.floor(Math.random() * 2) :
                       segment === 'Riskli' ? 3 + Math.floor(Math.random() * 7) :
                       1 + Math.floor(Math.random() * 4); // Uykuda
  
  const purchases = [];
  
  for (let i = 0; i < purchaseCount; i++) {
    const date = randomDate(new Date(2022, 0, 1), new Date());
    const amount = segment === 'Yüksek Değerli' ? 300 + Math.random() * 700 :
                  segment === 'Orta Değerli' ? 150 + Math.random() * 350 :
                  segment === 'Yeni Müşteri' ? 100 + Math.random() * 200 :
                  segment === 'Riskli' ? 100 + Math.random() * 300 :
                  50 + Math.random() * 150; // Uykuda
    
    const productCount = 1 + Math.floor(Math.random() * 5);
    const categories = generatePreferredCategories();
    
    purchases.push({
      id: `ORD${String(i+1).padStart(6, '0')}`,
      date,
      amount: Math.round(amount),
      productCount,
      categories,
      channel: randomItem(['Web', 'Mobil', 'Mağaza', 'Sosyal Medya', 'Çağrı Merkezi'])
    });
  }
  
  // Tarihe göre sırala (en son en üstte)
  return purchases.sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Müşteri kaybı riski hesaplama
function calculateChurnRisk(segment, recency, frequency, retentionRate) {
  // Temel risk faktörü
  let risk = 0;
  
  // Segment bazlı başlangıç risk puanı
  if (segment === 'Riskli') risk = 70;
  else if (segment === 'Uykuda') risk = 85;
  else if (segment === 'Orta Değerli') risk = 30;
  else if (segment === 'Yeni Müşteri') risk = 50;
  else risk = 10; // Yüksek Değerli
  
  // Son alışveriş zamanı etkisi (recency)
  // Daha uzun süre geçtikçe risk artar
  risk += recency * 0.5;
  
  // Alışveriş sıklığı etkisi (frequency)
  // Daha sık alışveriş yapan müşterilerin riski azalır
  risk -= frequency * 1.5;
  
  // Müşteri sadakati etkisi (retentionRate)
  // Daha sadık müşterilerin riski azalır
  risk -= retentionRate * 0.2;
  
  // Riski 0-100 aralığına sınırla
  risk = Math.max(0, Math.min(100, risk));
  
  return Math.round(risk);
}

// Müşteri Yaşam Boyu Değeri (CLV) hesaplama
function calculateCLV(monetaryValue, frequency, retentionRate) {
  // Basit CLV formülü: Ortalama Sipariş Değeri * Yıllık Sipariş Sayısı * Ortalama Müşteri Ömrü
  const avgOrderValue = monetaryValue / Math.max(1, frequency);
  const yearlyFrequency = Math.min(12, frequency * 1.5); // Yaklaşık yıllık sipariş tahmini
  const customerLifespan = (retentionRate / 100) * 5; // Retention rate'i 5 yıla yayılmış müşteri ömrüne dönüştür
  
  const clv = avgOrderValue * yearlyFrequency * customerLifespan;
  
  return Math.round(clv);
}

// Bir sonraki satın alma tahmini
function predictNextPurchase(segment, recency) {
  let days;
  
  if (segment === 'Yüksek Değerli') {
    days = 5 + Math.floor(Math.random() * 10);
  } else if (segment === 'Orta Değerli') {
    days = 10 + Math.floor(Math.random() * 20);
  } else if (segment === 'Yeni Müşteri') {
    days = 7 + Math.floor(Math.random() * 15);
  } else if (segment === 'Riskli') {
    days = 30 + Math.floor(Math.random() * 30);
  } else { // Uykuda
    days = 60 + Math.floor(Math.random() * 60);
  }
  
  // Recency'e göre ayarlama - uzun süredir alışveriş yapmadıysa tahminler daha uzak olur
  days += recency * 0.5;
  
  const date = new Date();
  date.setDate(date.getDate() + days);
  
  return date.toISOString().split('T')[0];
}

// Önerilen ürünler oluşturma
function generateRecommendedProducts(segment) {
  const productsByCategory = {
    'Elektronik': ['Akıllı Telefon', 'Tablet', 'Laptop', 'Kulaklık', 'Akıllı Saat', 'Bluetooth Hoparlör'],
    'Giyim': ['T-shirt', 'Pantolon', 'Ceket', 'Elbise', 'Ayakkabı', 'Çanta'],
    'Ev Eşyaları': ['Koltuk', 'Masa', 'Yatak', 'Ayna', 'Avize', 'Perde'],
    'Kozmetik': ['Parfüm', 'Ruj', 'Krem', 'Şampuan', 'Makyaj Seti', 'Güneş Kremi'],
    'Gıda': ['Kahve', 'Çay', 'Kuruyemiş', 'Çikolata', 'Zeytinyağı', 'Baharat'],
    'Aksesuar': ['Saat', 'Bileklik', 'Kolye', 'Güneş Gözlüğü', 'Kemer', 'Şapka']
  };
  
  const productCount = segment === 'Yüksek Değerli' ? 5 :
                     segment === 'Orta Değerli' ? 4 :
                     segment === 'Yeni Müşteri' ? 3 :
                     segment === 'Riskli' ? 3 :
                     2; // Uykuda
  
  const recommended = [];
  
  // Her kategoriden random ürün ekle
  const categories = Object.keys(productsByCategory);
  
  for (let i = 0; i < productCount; i++) {
    const category = categories[i % categories.length];
    const products = productsByCategory[category];
    const product = products[Math.floor(Math.random() * products.length)];
    
    // Ürün bilgisi
    recommended.push({
      id: `PROD${String(i+1).padStart(4, '0')}`,
      name: product,
      category: category,
      price: Math.round((50 + Math.random() * 450) * 10) / 10,
      matchScore: Math.round(50 + Math.random() * 50),
      discount: Math.random() < 0.3 ? Math.round(Math.random() * 30) : 0
    });
  }
  
  return recommended.sort((a, b) => b.matchScore - a.matchScore);
}

// Segment dağılımı için veri
const generateSegmentData = (customers) => {
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  const segmentCounts = {};
  
  // Her segment için müşteri sayısını hesapla
  segments.forEach(segment => {
    segmentCounts[segment] = customers.filter(c => c.segment === segment).length;
  });
  
  // Dairesel grafik için veriyi oluştur
  return segments.map(segment => ({
    name: segment,
    value: segmentCounts[segment],
    color: SEGMENT_COLORS[segment]
  }));
};

// RFM Analizi için veri
const generateRFMData = (customers) => {
  // Recency, Frequency ve Monetary değerlerini normalleştir
  const maxRecency = Math.max(...customers.map(c => c.recency));
  const maxFrequency = Math.max(...customers.map(c => c.frequency));
  const maxMonetaryValue = Math.max(...customers.map(c => c.monetaryValue));
  
  // Scatter plot için veriyi oluştur
  return customers.map(customer => ({
    id: customer.id,
    name: customer.name,
    segment: customer.segment,
    recency: customer.recency,
    frequency: customer.frequency,
    monetaryValue: customer.monetaryValue,
    rfmScore: Math.round(
      (1 - customer.recency / maxRecency) * 100 * 0.15 + 
      (customer.frequency / maxFrequency) * 100 * 0.35 + 
      (customer.monetaryValue / maxMonetaryValue) * 100 * 0.5
    )
  }));
};

// Müşteri segmentine göre değer analizi
const generateSegmentValueAnalysis = (customers) => {
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  
  // Her segment için toplam müşteri değerini hesapla
  const segmentValues = segments.map(segment => {
    const segmentCustomers = customers.filter(c => c.segment === segment);
    const customerCount = segmentCustomers.length;
    const totalValue = segmentCustomers.reduce((sum, c) => sum + c.monetaryValue, 0);
    const avgOrderValue = totalValue / customerCount;
    const totalPurchases = segmentCustomers.reduce((sum, c) => sum + c.frequency, 0);
    const avgFrequency = totalPurchases / customerCount;
    const totalCLV = segmentCustomers.reduce((sum, c) => sum + c.customerLifetimeValue, 0);
    const avgCLV = totalCLV / customerCount;
    const retentionRate = segmentCustomers.reduce((sum, c) => sum + c.retentionRate, 0) / customerCount;
    const churnRate = 100 - retentionRate;
    const valuePercentage = (totalValue / customers.reduce((sum, c) => sum + c.monetaryValue, 0)) * 100;
    
    return {
      segment,
      customerCount,
      percentOfTotal: (customerCount / customers.length) * 100,
      totalValue,
      avgOrderValue,
      avgFrequency,
      avgCLV,
      retentionRate,
      churnRate,
      valuePercentage
    };
  });
  
  return segmentValues;
};

// Müşteri yaşam döngüsü analizi
const generateLifecycleAnalysis = (customers) => {
  // Son 12 ayda satın alma eğilimleri
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const currentMonth = new Date().getMonth();
  
  const last12Months = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (currentMonth - 11 + i) % 12;
    return months[monthIndex];
  });
  
  // Her segment için satın alma trendi
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  
  return last12Months.map((month, index) => {
    // Her segment için rastgele ama gerçekçi satın alma değerleri
    const entry = { month };
    
    segments.forEach(segment => {
      const baseValue = segment === 'Yüksek Değerli' ? 120000 :
                        segment === 'Orta Değerli' ? 80000 :
                        segment === 'Yeni Müşteri' ? 40000 :
                        segment === 'Riskli' ? 30000 : 10000;
      
      // Mevsimsel dalgalanmalar ekle
      const seasonality = 1 + (Math.sin(index / 2) * 0.2);
      // Trend ekle - Riskli ve Uykuda segmentleri için azalan trend
      const trend = segment === 'Riskli' || segment === 'Uykuda' ? 
                    1 - (index * 0.02) : 
                    1 + (index * 0.01);
      
      entry[segment] = Math.round(baseValue * seasonality * trend);
    });
    
    return entry;
  });
};

// Kanal kullanım analizi
const generateChannelAnalysis = (customers) => {
  const channels = ['Web', 'Mobil', 'Mağaza', 'Sosyal Medya', 'Çağrı Merkezi'];
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  
  return channels.map(channel => {
    const entry = { channel };
    
    segments.forEach(segment => {
      // Her segment için farklı kanal tercihleri oluştur
      let value;
      
      if (segment === 'Yüksek Değerli') {
        if (channel === 'Mağaza' || channel === 'Web') value = 25 + Math.random() * 15;
        else if (channel === 'Mobil') value = 15 + Math.random() * 10;
        else value = 5 + Math.random() * 10;
      } else if (segment === 'Orta Değerli') {
        if (channel === 'Web' || channel === 'Mobil') value = 25 + Math.random() * 15;
        else if (channel === 'Mağaza') value = 15 + Math.random() * 10;
        else value = 5 + Math.random() * 10;
      } else if (segment === 'Yeni Müşteri') {
        if (channel === 'Mobil' || channel === 'Sosyal Medya') value = 30 + Math.random() * 20;
        else value = 5 + Math.random() * 15;
      } else if (segment === 'Riskli') {
        if (channel === 'Web') value = 20 + Math.random() * 20;
        else value = 10 + Math.random() * 15;
      } else { // Uykuda
        value = 5 + Math.random() * 15;
      }
      
      entry[segment] = Math.round(value);
    });
    
    // Her kanal için toplam değer hesapla
    entry.total = segments.reduce((sum, segment) => sum + entry[segment], 0);
    
    return entry;
  });
};

// Kategori tercih analizi
const generateCategoryPreferences = (customers) => {
  const categories = ['Elektronik', 'Giyim', 'Ev Eşyaları', 'Kozmetik', 'Gıda', 'Aksesuar'];
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  
  return categories.map(category => {
    const entry = { category };
    
    segments.forEach(segment => {
      // Her segment için farklı kategori tercihleri oluştur
      let value;
      
      if (segment === 'Yüksek Değerli') {
        if (category === 'Elektronik' || category === 'Ev Eşyaları') value = 25 + Math.random() * 15;
        else value = 10 + Math.random() * 15;
      } else if (segment === 'Orta Değerli') {
        if (category === 'Giyim' || category === 'Kozmetik') value = 20 + Math.random() * 15;
        else value = 10 + Math.random() * 15;
      } else if (segment === 'Yeni Müşteri') {
        if (category === 'Giyim' || category === 'Aksesuar') value = 25 + Math.random() * 15;
        else value = 5 + Math.random() * 15;
      } else if (segment === 'Riskli') {
        if (category === 'Gıda' || category === 'Elektronik') value = 20 + Math.random() * 15;
        else value = 10 + Math.random() * 10;
      } else { // Uykuda
        value = 5 + Math.random() * 15;
      }
      
      entry[segment] = Math.round(value);
    });
    
    // Her kategori için toplam değer hesapla
    entry.total = segments.reduce((sum, segment) => sum + entry[segment], 0);
    
    return entry;
  });
};

// Müşteri kazanımı ve kaybı analizi
const generateCustomerAcquisitionChurnData = () => {
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const currentMonth = new Date().getMonth();
  
  return Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (currentMonth - 11 + i) % 12;
    const month = months[monthIndex];
    
    // Gerçekçi kazanım ve kayıp sayıları - zamanla artan yeni müşteri eğilimi
    const acquired = Math.round(80 + i * 2 + Math.random() * 40);
    
    // Kaybedilen müşteri sayısı - genellikle yeni müşterilerden daha az
    const churn = Math.round(45 + Math.random() * 30 - i * 0.5);
    
    // Net büyüme
    const netGrowth = acquired - churn;
    
    return {
      month,
      acquired,
      churn,
      netGrowth,
      retentionRate: Math.round(85 + Math.random() * 10 - (churn / 10))
    };
  });
};

// Satın alma frekansı dağılımı
const generatePurchaseFrequencyDistribution = (customers) => {
  // Frekans grupları
  const frequencyRanges = [
    { range: '1', min: 1, max: 1 },
    { range: '2-3', min: 2, max: 3 },
    { range: '4-6', min: 4, max: 6 },
    { range: '7-12', min: 7, max: 12 },
    { range: '13+', min: 13, max: 100 }
  ];
  
  return frequencyRanges.map(range => {
    const customersInRange = customers.filter(c => 
      c.frequency >= range.min && c.frequency <= range.max
    );
    
    return {
      range: range.range,
      count: customersInRange.length,
      percentage: (customersInRange.length / customers.length) * 100,
      avgOrderValue: customersInRange.reduce((sum, c) => sum + c.monetaryValue, 0) / Math.max(1, customersInRange.length) / Math.max(1, customersInRange[0]?.frequency || 1)
    };
  });
};

// Toplam ve ortalama değer hesapla
const calculateSummaryMetrics = (customers) => {
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.recency < 30).length;
  const inactiveCustomers = totalCustomers - activeCustomers;
  const totalValue = customers.reduce((sum, customer) => sum + customer.monetaryValue, 0);
  const avgValue = totalValue / totalCustomers;
  const avgEngagementScore = customers.reduce((sum, customer) => sum + customer.engagementScore, 0) / totalCustomers;
  const avgRetentionRate = customers.reduce((sum, customer) => sum + customer.retentionRate, 0) / totalCustomers;
  const totalOrders = customers.reduce((sum, c) => sum + c.frequency, 0);
  const avgOrdersPerCustomer = totalOrders / totalCustomers;
  const totalCLV = customers.reduce((sum, c) => sum + c.customerLifetimeValue, 0);
  const avgCLV = totalCLV / totalCustomers;
  
  // Segment değerleri
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  const segmentValues = {};
  
  segments.forEach(segment => {
    const segmentCustomers = customers.filter(c => c.segment === segment);
    segmentValues[segment] = {
      count: segmentCustomers.length,
      percentage: (segmentCustomers.length / totalCustomers) * 100,
      avgValue: segmentCustomers.reduce((sum, c) => sum + c.monetaryValue, 0) / segmentCustomers.length,
      totalValue: segmentCustomers.reduce((sum, c) => sum + c.monetaryValue, 0),
      valuePercentage: (segmentCustomers.reduce((sum, c) => sum + c.monetaryValue, 0) / totalValue) * 100
    };
  });
  
  return {
    totalCustomers,
    activeCustomers,
    inactiveCustomers,
    activityRate: (activeCustomers / totalCustomers) * 100,
    totalValue,
    avgValue: Math.round(avgValue),
    avgEngagementScore: Math.round(avgEngagementScore),
    avgRetentionRate: Math.round(avgRetentionRate),
    totalOrders,
    avgOrdersPerCustomer,
    totalCLV,
    avgCLV,
    segmentValues
  };
};

// Müşteri churn risk dağılımı
const generateChurnRiskDistribution = (customers) => {
  const riskGroups = [
    { name: 'Düşük', min: 0, max: 30, color: '#4caf50' },
    { name: 'Orta', min: 31, max: 60, color: '#ff9800' },
    { name: 'Yüksek', min: 61, max: 100, color: '#f44336' }
  ];
  
  return riskGroups.map(group => {
    const customersInGroup = customers.filter(c => 
      c.churnRisk >= group.min && c.churnRisk <= group.max
    );
    
    return {
      name: group.name,
      value: customersInGroup.length,
      color: group.color,
      customers: customersInGroup
    };
  });
};

// Müşteri satın alma sıklığı ve son alışveriş süresi analizi (RF Matrisi için)
const generateRFMatrix = (customers) => {
  // R (Recency) grupları - son alışveriş günü
  const rGroups = [
    { name: 'R5 (0-7 gün)', min: 0, max: 7 },
    { name: 'R4 (8-14 gün)', min: 8, max: 14 },
    { name: 'R3 (15-30 gün)', min: 15, max: 30 },
    { name: 'R2 (31-60 gün)', min: 31, max: 60 },
    { name: 'R1 (61+ gün)', min: 61, max: 1000 }
  ];
  
  // F (Frequency) grupları - alışveriş sayısı
  const fGroups = [
    { name: 'F5 (13+ sipariş)', min: 13, max: 1000 },
    { name: 'F4 (7-12 sipariş)', min: 7, max: 12 },
    { name: 'F3 (4-6 sipariş)', min: 4, max: 6 },
    { name: 'F2 (2-3 sipariş)', min: 2, max: 3 },
    { name: 'F1 (1 sipariş)', min: 1, max: 1 }
  ];
  
  // RF Matrisini oluştur
  const rfMatrix = [];
  
  rGroups.forEach(rGroup => {
    fGroups.forEach(fGroup => {
      const customersInCell = customers.filter(c => 
        c.recency >= rGroup.min && c.recency <= rGroup.max &&
        c.frequency >= fGroup.min && c.frequency <= fGroup.max
      );
      
      // Hücre içindeki müşterilerin ortalama değerleri
      const avgValue = customersInCell.length > 0 
        ? customersInCell.reduce((sum, c) => sum + c.monetaryValue, 0) / customersInCell.length 
        : 0;
      
      const avgCLV = customersInCell.length > 0 
        ? customersInCell.reduce((sum, c) => sum + c.customerLifetimeValue, 0) / customersInCell.length 
        : 0;
      
      // Segment dağılımı
      const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
      const segmentCounts = {};
      segments.forEach(segment => {
        segmentCounts[segment] = customersInCell.filter(c => c.segment === segment).length;
      });
      
      // Baskın segment
      let dominantSegment = '';
      let maxCount = -1;
      Object.entries(segmentCounts).forEach(([segment, count]) => {
        if (count > maxCount) {
          maxCount = count;
          dominantSegment = segment;
        }
      });
      
      rfMatrix.push({
        rGroup: rGroup.name.split(' ')[0],
        fGroup: fGroup.name.split(' ')[0],
        rGroupFull: rGroup.name,
        fGroupFull: fGroup.name,
        customerCount: customersInCell.length,
        percentOfTotal: (customersInCell.length / customers.length) * 100,
        avgValue,
        avgCLV,
        segmentCounts,
        dominantSegment
      });
    });
  });
  
  return rfMatrix;
};

// Müşteri değer segmentasyonu için radar grafik verisi
const generateCustomerValueRadar = (customers) => {
  const segments = ['Yüksek Değerli', 'Orta Değerli', 'Yeni Müşteri', 'Riskli', 'Uykuda'];
  const dimensions = ['Etkileşim', 'Bağlılık', 'İşlem Hacmi', 'Alışveriş Sıklığı', 'Ortalama Sepet'];
  
  // Segment bazında her boyut için ortalama değerler
  return segments.map(segment => {
    const segmentCustomers = customers.filter(c => c.segment === segment);
    
    // Boyutlar için değer hesaplama
    // Değerler 0-100 arasında normalize edilmeli
    const engagementScore = segmentCustomers.reduce((sum, c) => sum + c.engagementScore, 0) / segmentCustomers.length;
    const loyalty = segmentCustomers.reduce((sum, c) => sum + c.retentionRate, 0) / segmentCustomers.length;
    const transactionVolume = (segmentCustomers.reduce((sum, c) => sum + c.monetaryValue, 0) / segmentCustomers.length) / 100;
    const purchaseFrequency = (segmentCustomers.reduce((sum, c) => sum + c.frequency, 0) / segmentCustomers.length) * 10;
    const avgOrderValue = transactionVolume / Math.max(1, purchaseFrequency) * 20;
    
    return {
      segment,
      data: [
        { dimension: dimensions[0], value: engagementScore },
        { dimension: dimensions[1], value: loyalty },
        { dimension: dimensions[2], value: Math.min(100, transactionVolume) },
        { dimension: dimensions[3], value: Math.min(100, purchaseFrequency) },
        { dimension: dimensions[4], value: Math.min(100, avgOrderValue) }
      ]
    };
  });
};

// Segment verilerine dış etiketler ekle
const customizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
  const RADIAN = Math.PI / 180;
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill={SEGMENT_COLORS[name]} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {name} ({(percent * 100).toFixed(0)}%)
    </text>
  );
};

// Ana Bileşen
const MusteriDavranisAnalizi = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({});
  const [segmentData, setSegmentData] = useState([]);
  const [rfmData, setRFMData] = useState([]);
  const [segmentValueAnalysis, setSegmentValueAnalysis] = useState([]);
  const [lifecycleAnalysis, setLifecycleAnalysis] = useState([]);
  const [channelAnalysis, setChannelAnalysis] = useState([]);
  const [categoryPreferences, setCategoryPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedRFMFilters, setSelectedRFMFilters] = useState({
    recency: [0, 120],
    frequency: [0, 30],
    monetary: [0, 10000]
  });
  const [showInactiveCustomers, setShowInactiveCustomers] = useState(false);
  
  // Veri yükleme simülasyonu
  useEffect(() => {
    setLoading(true);
    
    // Simüle edilmiş yükleme gecikmesi
    setTimeout(() => {
      const customersData = generateCustomerData();
      setCustomers(customersData);
      
      const segmentData = generateSegmentData(customersData);
      setSegmentData(segmentData);
      
      const rfmData = generateRFMData(customersData);
      setRFMData(rfmData);
      
      const segmentValueData = generateSegmentValueAnalysis(customersData);
      setSegmentValueAnalysis(segmentValueData);
      
      const lifecycleData = generateLifecycleAnalysis(customersData);
      setLifecycleAnalysis(lifecycleData);
      
      const channelData = generateChannelAnalysis(customersData);
      setChannelAnalysis(channelData);
      
      const categoryData = generateCategoryPreferences(customersData);
      setCategoryPreferences(categoryData);
      
      const metrics = calculateSummaryMetrics(customersData);
      setSummaryMetrics(metrics);
      
      setLoading(false);
    }, 1500);
  }, []);
  
  // Sekme değişimi işleyicisi
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Segment filtresi değişimi
  const handleSegmentChange = (event) => {
    setSelectedSegment(event.target.value);
  };
  
  // RFM filtresi değişimi
  const handleRFMFilterChange = (filter, value) => {
    setSelectedRFMFilters(prevFilters => ({
      ...prevFilters,
      [filter]: value
    }));
  };
  
  // Pasif müşterileri gösterme/gizleme
  const handleInactiveToggle = (event) => {
    setShowInactiveCustomers(event.target.checked);
  };
  
  // Filtrelenmiş müşterileri al
  const getFilteredCustomers = () => {
    let filtered = [...customers];
    
    // Segment filtreleme
    if (selectedSegment !== 'all') {
      filtered = filtered.filter(customer => customer.segment === selectedSegment);
    }
    
    // RFM filtreleme
    filtered = filtered.filter(customer => 
      customer.recency >= selectedRFMFilters.recency[0] && 
      customer.recency <= selectedRFMFilters.recency[1] &&
      customer.frequency >= selectedRFMFilters.frequency[0] && 
      customer.frequency <= selectedRFMFilters.frequency[1] &&
      customer.monetaryValue >= selectedRFMFilters.monetary[0] && 
      customer.monetaryValue <= selectedRFMFilters.monetary[1]
    );
    
    // Pasif müşteri filtreleme
    if (!showInactiveCustomers) {
      filtered = filtered.filter(customer => customer.segment !== 'Uykuda');
    }
    
    return filtered;
  };
  
  // Para birimi formatı
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value);
  };

  // Segment rengini al
  const getSegmentColor = (segment) => {
    return SEGMENT_COLORS[segment] || '#757575';
  };

  // Segment verilerine dış etiketler ekle
  const customizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = 25 + innerRadius + (outerRadius - innerRadius);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill={SEGMENT_COLORS[name]} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };
  
  // Önerilen aksiyon mesajını oluştur
  const getActionMessage = (segment) => {
    switch (segment) {
      case 'Yüksek Değerli': return 'VIP hizmetler ve özel teklifler sunun';
      case 'Orta Değerli': return 'Çapraz satış ve üst satış fırsatlarını değerlendirin';
      case 'Yeni Müşteri': return 'İlk deneyimlerini mükemmel hale getirin ve ilişkiyi güçlendirin';
      case 'Riskli': return 'Özel kampanyalarla yeniden kazanın ve memnuniyeti artırın';
      case 'Uykuda': return 'Yeniden aktivasyon kampanyaları ile ilgiyi canlandırın';
      default: return 'Müşteri davranışlarını analiz edin ve özelleştirilmiş aksiyon alın';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Müşteri Davranış Analizi
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Müşteri verilerini analiz edin, davranış paternlerini keşfedin ve kişiselleştirilmiş pazarlama stratejileri geliştirin.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Yükleniyor durumu */}
      {loading && (
        <Box sx={{ width: '100%', my: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Müşteri verileri analiz ediliyor...
          </Typography>
        </Box>
      )}

      {!loading && (
        <>
          {/* Özet Kartlar */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Toplam Müşteriler
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber 
                          value={summaryMetrics.totalCustomers} 
                          duration={1500}
                        />
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#3f51b5', width: 48, height: 48 }}>
                      <GroupIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Ortalama Müşteri Değeri */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ortalama Değer
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber 
                          value={summaryMetrics.avgValue} 
                          duration={1500}
                          formatter={(value) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(value)}
                        />
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                      <ShoppingCartIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Etkileşim Skoru */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Etkileşim Skoru
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber 
                          value={summaryMetrics.avgEngagementScore} 
                          duration={1500}
                          suffix="/100"
                        />
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                      <AssessmentIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Elde Tutma Oranı */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Elde Tutma Oranı
                      </Typography>
                      <Typography variant="h4" component="div" fontWeight="bold">
                        <AnimatedNumber 
                          value={summaryMetrics.avgRetentionRate} 
                          duration={1500}
                          suffix="%"
                        />
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#e91e63', width: 48, height: 48 }}>
                      <TrendingUpIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Ana Sekmeler - handleTabChange fonksiyonuyla bağlantı kuruyoruz */}
          <Paper sx={{ borderRadius: 2, mb: 4 }}>
            <Tabs
              value={activeTab}  // Değer olarak activeTab state'i kullanıyoruz
              onChange={handleTabChange}  // onChange işleyicisi olarak handleTabChange fonksiyonunu kullanıyoruz
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Müşteri Segmentasyonu" 
                icon={<PersonIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Davranış Analizi" 
                icon={<AssessmentIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Kanal Tercihleri" 
                icon={<GroupIcon />} 
                iconPosition="start" 
              />
            </Tabs>
          </Paper>
          
          {/* Seçilen Sekmeye göre içeriği göster */}
          {activeTab === 0 && (
            // Müşteri Segmentasyonu için içerik
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Müşteri Segment Dağılımı</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Müşterilerinizin değer, davranış ve satın alma alışkanlıklarına göre segmentleri.
                    Bu analiz, hedefli pazarlama ve ürün geliştirme stratejilerinizi yönlendirmenize yardımcı olur.
                  </Typography>
                  <Box sx={{ height: 400, mt: 4 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={segmentData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={150}
                          fill="#8884d8"
                          dataKey="value"
                          label={customizedPieLabel}
                        >
                          {segmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value) => `${value} Müşteri`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Segment Özeti</Typography>
                  <Box sx={{ mt: 2 }}>
                    {segmentData.map((segment) => (
                      <Box 
                        key={segment.name}
                        sx={{ 
                          mb: 2, 
                          p: 2, 
                          borderRadius: 1, 
                          bgcolor: `${segment.color}10`,
                          border: `1px solid ${segment.color}30`
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ color: segment.color, fontWeight: 600 }}>
                            {segment.name}
                          </Typography>
                          <Chip 
                            label={`${segment.value} Müşteri`}
                            size="small"
                            sx={{ bgcolor: `${segment.color}20`, color: segment.color }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          {getActionMessage(segment.name)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            // Davranış Analizi için içerik - Geliştirilmiş içerik
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Satın Alma Davranış Analizi</Typography>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Segment Filtresi</InputLabel>
                      <Select
                        value={selectedSegment}
                        onChange={handleSegmentChange}
                        label="Segment Filtresi"
                      >
                        <MenuItem value="all">Tüm Müşteriler</MenuItem>
                        <MenuItem value="Yüksek Değerli">Yüksek Değerli</MenuItem>
                        <MenuItem value="Orta Değerli">Orta Değerli</MenuItem>
                        <MenuItem value="Yeni Müşteri">Yeni Müşteri</MenuItem>
                        <MenuItem value="Riskli">Riskli</MenuItem>
                        <MenuItem value="Uykuda">Uykuda</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Müşterilerinizin satın alma sıklığı ve tutarları arasındaki ilişkiyi inceleyin.
                    Bu grafik, farklı segment davranışlarını karşılaştırmanızı sağlar.
                  </Typography>
                  <Box sx={{ height: 400, mt: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={lifecycleAnalysis}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="Yüksek Değerli" 
                          stroke={SEGMENT_COLORS['Yüksek Değerli']} 
                          strokeWidth={2} 
                          activeDot={{ r: 8 }} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Orta Değerli" 
                          stroke={SEGMENT_COLORS['Orta Değerli']} 
                          strokeWidth={2} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Yeni Müşteri" 
                          stroke={SEGMENT_COLORS['Yeni Müşteri']} 
                          strokeWidth={2} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Riskli" 
                          stroke={SEGMENT_COLORS['Riskli']} 
                          strokeWidth={2} 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Uykuda" 
                          stroke={SEGMENT_COLORS['Uykuda']} 
                          strokeWidth={2} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Elde Tutma ve Kaybetme Riski</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Müşterilerinizin kaybedilme riskini ve elde tutma oranlarını inceleyin.
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Segment</TableCell>
                          <TableCell align="right">Elde Tutma</TableCell>
                          <TableCell align="right">Kaybetme Riski</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {segmentValueAnalysis.map((segment) => (
                          <TableRow key={segment.segment}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box 
                                  sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    borderRadius: '50%', 
                                    bgcolor: SEGMENT_COLORS[segment.segment],
                                    mr: 1 
                                  }} 
                                />
                                {segment.segment}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{segment.retentionRate.toFixed(1)}%</TableCell>
                            <TableCell align="right">{segment.churnRate.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Müşteri Değer Analizi
                    </Typography>
                    <Box sx={{ height: 300, mt: 2 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={segmentValueAnalysis}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="segment" />
                          <YAxis />
                          <RechartsTooltip formatter={(value) => `₺${new Intl.NumberFormat('tr-TR').format(value)}`} />
                          <Bar dataKey="avgOrderValue" name="Ortalama Sipariş" fill="#3f51b5" />
                          <Bar dataKey="avgCLV" name="Müşteri Ömür Değeri" fill="#4caf50" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Müşteri Segment Özellik Analizi</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Her bir müşteri segmentinin 5 temel boyuttaki performansı. Bu radar grafik, 
                    segmentler arasındaki farkları görselleştirmenize yardımcı olur.
                  </Typography>
                  <Box sx={{ height: 400, mt: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart outerRadius={150} width={730} height={400}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="dimension" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        
                        {generateCustomerValueRadar(customers).map((segment) => (
                          <Radar
                            key={segment.segment}
                            name={segment.segment}
                            dataKey="value"
                            stroke={SEGMENT_COLORS[segment.segment]}
                            fill={SEGMENT_COLORS[segment.segment]}
                            fillOpacity={0.2}
                            data={segment.data}
                          />
                        ))}
                        
                        <Legend />
                        <RechartsTooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            // Kanal Tercihleri için içerik - Geliştirilmiş içerik
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Kanal Tercihleri Analizi</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Müşterilerinizin tercih ettiği alışveriş kanallarını segment bazında görüntüleyin.
                  </Typography>
                  <Box sx={{ height: 400, mt: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={channelAnalysis}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="channel" />
                        <YAxis />
                        <RechartsTooltip formatter={(value) => `${value}%`} />
                        <Legend />
                        <Bar dataKey="Yüksek Değerli" stackId="a" fill={SEGMENT_COLORS['Yüksek Değerli']} />
                        <Bar dataKey="Orta Değerli" stackId="a" fill={SEGMENT_COLORS['Orta Değerli']} />
                        <Bar dataKey="Yeni Müşteri" stackId="a" fill={SEGMENT_COLORS['Yeni Müşteri']} />
                        <Bar dataKey="Riskli" stackId="a" fill={SEGMENT_COLORS['Riskli']} />
                        <Bar dataKey="Uykuda" stackId="a" fill={SEGMENT_COLORS['Uykuda']} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Kanal Performansı</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Farklı kanallardan gerçekleştirilen ortalama işlem değerleri ve dönüşüm oranları.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Kanal</TableCell>
                          <TableCell align="right">Ortalama Değer</TableCell>
                          <TableCell align="right">Dönüşüm Oranı</TableCell>
                          <TableCell>Trend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          { channel: 'Web', avgValue: 820, conversionRate: 5.4, trend: 'up' },
                          { channel: 'Mobil', avgValue: 560, conversionRate: 4.2, trend: 'up' },
                          { channel: 'Mağaza', avgValue: 1250, conversionRate: 8.7, trend: 'down' },
                          { channel: 'Sosyal Medya', avgValue: 390, conversionRate: 2.8, trend: 'up' },
                          { channel: 'Çağrı Merkezi', avgValue: 980, conversionRate: 7.5, trend: 'stable' }
                        ].map((channel) => (
                          <TableRow key={channel.channel}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {channel.channel === 'Web' && <ComputerIcon sx={{ mr: 1, fontSize: 20, color: CHANNEL_COLORS['Web'] }} />}
                                {channel.channel === 'Mobil' && <PhoneAndroidIcon sx={{ mr: 1, fontSize: 20, color: CHANNEL_COLORS['Mobil'] }} />}
                                {channel.channel === 'Mağaza' && <StorefrontIcon sx={{ mr: 1, fontSize: 20, color: CHANNEL_COLORS['Mağaza'] }} />}
                                {channel.channel === 'Sosyal Medya' && <CampaignIcon sx={{ mr: 1, fontSize: 20, color: CHANNEL_COLORS['Sosyal Medya'] }} />}
                                {channel.channel === 'Çağrı Merkezi' && <GroupIcon sx={{ mr: 1, fontSize: 20, color: CHANNEL_COLORS['Çağrı Merkezi'] }} />}
                                {channel.channel}
                              </Box>
                            </TableCell>
                            <TableCell align="right">₺{channel.avgValue}</TableCell>
                            <TableCell align="right">%{channel.conversionRate}</TableCell>
                            <TableCell>
                              {channel.trend === 'up' && <TrendingUpIcon color="success" />}
                              {channel.trend === 'down' && <TrendingDownIcon color="error" />}
                              {channel.trend === 'stable' && <TrendingFlatIcon color="primary" />}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Kategori Tercihi ve Kanal İlişkisi</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Müşterilerinizin farklı ürün kategorileri için tercih ettiği kanalları inceleyin.
                  </Typography>
                  <Box sx={{ height: 400, mt: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={categoryPreferences}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis />
                        <RechartsTooltip formatter={(value) => `${value}%`} />
                        <Legend />
                        <Bar dataKey="Yüksek Değerli" name="Yüksek Değerli Müşteriler" stackId="a" fill={SEGMENT_COLORS['Yüksek Değerli']} />
                        <Bar dataKey="Orta Değerli" name="Orta Değerli Müşteriler" stackId="a" fill={SEGMENT_COLORS['Orta Değerli']} />
                        <Bar dataKey="Yeni Müşteri" name="Yeni Müşteriler" stackId="a" fill={SEGMENT_COLORS['Yeni Müşteri']} />
                        <Bar dataKey="Riskli" name="Riskli Müşteriler" stackId="a" fill={SEGMENT_COLORS['Riskli']} />
                        <Bar dataKey="Uykuda" name="Uykuda Müşteriler" stackId="a" fill={SEGMENT_COLORS['Uykuda']} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default MusteriDavranisAnalizi;

// TrendingDownIcon bileşeni
function TrendingDownIcon(props) {
  return <Box component="span" {...props} sx={{ fontSize: '1.2rem', ...props.sx }}>↓</Box>;
}

// TrendingFlatIcon bileşeni
function TrendingFlatIcon(props) {
  return <Box component="span" {...props} sx={{ fontSize: '1.2rem', ...props.sx }}>↔</Box>;
}
